import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { I18ConfigService } from './I18-config.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { TxService } from './tx.service';
import { readAffiliateSession } from './travelverse-sso.util';

export interface AffiliateContext {
  clickId?: string;
  objectName?: string;
  objectType?: string;
  language?: string;
  currency?: string;
  country?: string;
  city?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  tierId?: string;
  landedAt?: string;
}

/**
 * Reads the affiliate landing context (captured by captureTravelverseSsoFromUrl into the
 * sessionStorage attribution session) and applies its side effects: locale + currency,
 * an analytics landing event, and a fire-and-forget beacon to the backend. The session
 * retires when the tab closes or 24h after landing (see readAffiliateSession).
 *
 * Per-flow search pre-selection consumes this context separately (see consume/markConsumed).
 */
@Injectable({ providedIn: 'root' })
export class AffiliateContextService {
  private readonly affiliateLandingApi =
    environment.txBaseUrl + '/api/affiliateLanding';

  constructor(
    private i18Config: I18ConfigService,
    private analytics: AnalyticsService,
    private txService: TxService,
    private http: HttpClient
  ) {}

  getContext(): AffiliateContext {
    // Source of truth is the sessionStorage attribution session (retires on tab close + 24h cap),
    // so pre-select / beacon / analytics all stop seeing the landing once it's retired.
    return (readAffiliateSession() as AffiliateContext) || {};
  }

  getClickId(): string | undefined {
    return this.getContext().clickId;
  }

  hasLanding(): boolean {
    const ctx = this.getContext();
    return !!(ctx.objectName || ctx.objectType);
  }

  /**
   * Runs the one-time side effects for a fresh landing. Idempotent per clickId (guarded
   * in sessionStorage) so the currency-driven reload can't loop. Call once on bootstrap
   * after the URL params have been captured.
   */
  processLanding(): void {
    const ctx = this.getContext();
    if (!ctx.clickId) {
      return;
    }
    const guardKey = 'affiliate_processed_' + ctx.clickId;
    if (sessionStorage.getItem(guardKey)) {
      return;
    }
    sessionStorage.setItem(guardKey, '1');

    // 1) Language — setLanguage() already falls back to 'en' for unsupported codes.
    if (ctx.language) {
      try {
        this.i18Config.setLanguage(ctx.language);
      } catch {
        /* non-fatal */
      }
    }

    // 2) Analytics landing event + 3) backend beacon (before any reload).
    this.trackLanding(ctx);
    this.sendBeacon(ctx);

    // 4) Currency LAST — setCurrency() triggers a full app reload. Only when the partner
    // passed one that differs from the current selection. URL params are already stripped,
    // so the post-reload bootstrap won't re-enter this path.
    if (ctx.currency && ctx.currency !== this.i18Config.getCurrency()) {
      this.i18Config.setCurrency(ctx.currency);
    }
  }

  private trackLanding(ctx: AffiliateContext): void {
    try {
      this.analytics.trackUnified('affiliate_landing', {
        flow: 'affiliate',
        step: 'landing',
        step_index: 0,
        outcome: 'na',
        affiliate_country: ctx.country,
        affiliate_city: ctx.city,
      });
    } catch {
      /* analytics must never block bootstrap */
    }
  }

  private sendBeacon(ctx: AffiliateContext): void {
    // key + pid go on the query string (the backend resolves the partner from query params),
    // the rest of the standard params + attribution travel in the POST body.
    const { key, pid, ...rest } = this.txService.getDefaultApiParams();
    // Use the language/currency from the landing URL when present (the app default may not
    // reflect them yet — currency in particular is only applied via a post-beacon reload).
    const language = ctx.language || rest.lang;
    const currency = ctx.currency || rest.currency;
    const url =
      `${this.affiliateLandingApi}?key=${encodeURIComponent(key ?? '')}` +
      `&pid=${encodeURIComponent(pid ?? '')}`;
    const payload = {
      ...rest, // platform, client, lang, currency
      lang: language,
      currency,
      language,
      clickId: ctx.clickId,
      objectName: ctx.objectName,
      objectType: ctx.objectType,
      country: ctx.country,
      city: ctx.city,
      address: ctx.address,
      latitude: ctx.latitude,
      longitude: ctx.longitude,
      referrer: document.referrer || undefined,
    };
    // Fire-and-forget; a failed beacon must never affect the user's session.
    this.http.post(url, payload).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  // --- Search pre-select consumption -------------------------------------------------
  // A landing context is "consumable" once per landing for search pre-selection. Each
  // flow checks isConsumable() and calls markConsumed() after it applies the prefill,
  // so later manual navigation isn't overridden.
  isConsumable(): boolean {
    const ctx = this.getContext();
    if (!ctx.clickId) return false;
    return !sessionStorage.getItem('affiliate_preselect_' + ctx.clickId);
  }

  markConsumed(): void {
    const clickId = this.getClickId();
    if (clickId) {
      sessionStorage.setItem('affiliate_preselect_' + clickId, '1');
    }
  }
}
